import { appsFlyerService, snapchatService, gTagService, store } from 'store'
import { initTwitterService, revokeTwitterConsent } from 'Services/Twitter.service'
import { initTaboolaService, revokeTaboolaConsent } from 'Services/Taboola.service'
import { grantSignalsGatewayConsent, revokeSignalsGatewayConsent } from 'Services/SignalsGateway.service'
import { grantFacebookConsent, revokeFacebookConsent } from 'Services/Facebook.service'
import { grantTikTokConsent, revokeTikTokConsent } from 'Services/TikTok.service'
import { grantOpenAiAdsConsent, revokeOpenAiAdsConsent } from 'Services/OpenAiAds.service'
import { initNewsBreakService, revokeNewsBreakConsent } from './NewsBreak.service'

export enum CookieCategory {
  STRICTLY_NECESSARY = 'C0001',
  PERFORMANCE = 'C0002',
  FUNCTIONAL = 'C0003',
  TARGETING = 'C0004',
  SOCIAL_MEDIA = 'C0005',
  VIDEO = 'VID1'
}

let cookieConsentGranted = false

const handleConsentChange = (cookies: string | string[]) => {
  if (typeof cookies === 'string') {
    cookies = cookies.split(',')
  }

  // Google first: the consent events below leave under the new state, so a
  // withdrawal's own event never carries the earlier grant.
  gTagService.updateConsent(
    cookies.includes(CookieCategory.PERFORMANCE),
    cookies.includes(CookieCategory.TARGETING)
  )

  logEvent('web_cookie_consent_update', {
    performance: cookies.includes(CookieCategory.PERFORMANCE) ? 'granted' : 'denied',
    targeting: cookies.includes(CookieCategory.TARGETING) ? 'granted' : 'denied'
  })
  if (!cookieConsentGranted &&
    cookies.includes(CookieCategory.PERFORMANCE) &&
    cookies.includes(CookieCategory.TARGETING)) {
    logEvent('web_cookie_consent_granted')
    cookieConsentGranted = true
  }

  /* ------------------ Handle Performance Cookies -------------------- */

  // Only the GTag services (above) use performance cookies.

  /* ------------------ Handle Targeting Cookies -------------------- */

  // AppsFlyer (web) is an advertising vendor: targeting in every region.
  if (cookies.includes(CookieCategory.TARGETING)) {
    appsFlyerService?.grantConsent()
    grantFacebookConsent()
    initTwitterService(store)
    initNewsBreakService(store)
    initTaboolaService()
    // initPinterestService()
    snapchatService.initializePixel()
    grantSignalsGatewayConsent()
    grantTikTokConsent()
    grantOpenAiAdsConsent()
  } else {
    appsFlyerService?.revokeConsent()
    revokeFacebookConsent()
    revokeTwitterConsent()
    revokeTaboolaConsent()
    revokeNewsBreakConsent()
    // revokePinterestService()
    snapchatService.revokeConsent()
    revokeSignalsGatewayConsent()
    revokeTikTokConsent()
    revokeOpenAiAdsConsent()
  }
}

// Sentry gets import failures, a receipt's first server error (consent.js
// marks it `first`; builds without `attempt` report every one) and the weekly
// count of receipts given up; network-level receipt and geo failures are
// expected noise and only counted.
const logConsentEvent = ({ name, params }: BfConsentEvent) => {
  switch (name) {
    case 'bannerShown': return logEvent('web_consent_bannerShown', params)
    case 'decision': return logEvent('web_consent_decision', params)
    case 'geoFailed': return logEvent('web_consent_geoFailed', params)
    case 'importFailed': return logError('web_consent_importError', name, params, true)
    case 'receiptFailed':
      if (params.reason === 'abandoned') return logError('web_consent_receiptAbandonedError', name, params, true)
      if (params.reason?.startsWith('http_') && (params.first === '1' || params.attempt === undefined)) return logError('web_consent_receiptError', name, params, true)
      return logEvent('web_consent_receiptFailed', params)
  }
}

const SCRIPT_MISSING_REPORTED_KEY = 'bf_consent_missing_reported'
const SCRIPT_MISSING_REPORT_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000

// A blocked consent.js recurs on every load: counted each time, sent to Sentry
// at most once a week per browser.
const reportScriptMissing = () => {
  let toSentry = true
  try {
    const last = Number(localStorage.getItem(SCRIPT_MISSING_REPORTED_KEY))
    toSentry = !(last > 0 && Date.now() - last < SCRIPT_MISSING_REPORT_INTERVAL_MS)
    if (toSentry) localStorage.setItem(SCRIPT_MISSING_REPORTED_KEY, String(Date.now()))
  } catch {}
  logError('web_consent_scriptMissingError', 'bfConsent is not defined', {}, toSentry)
}

let isCookieServiceInitiated = false

// consent.js loads synchronously before the app bundle: bfConsent is either
// there or failed to load, in which case no consent applies.
export const initCookieService = () => {
  if (isCookieServiceInitiated) return
  isCookieServiceInitiated = true
  const consent = window.bfConsent
  if (!consent) {
    reportScriptMissing()
    return
  }
  consent.onEvent(logConsentEvent)
  // consent.js also delivers when only the status or profile changed (e.g. the
  // Global notice closing). Vendors are applied for the first state, changed
  // categories and every decision; without a delivery reason (older
  // consent.js), for every delivery.
  let appliedGroups: string | null = null
  consent.onChange((state, delivery) => {
    const groups = [...state.groups].sort().join(',')
    const repeat = (delivery?.reason === 'snapshot' || delivery?.reason === 'state') && groups === appliedGroups
    appliedGroups = groups
    if (repeat) return
    handleConsentChange(state.groups)
  })
}
